/**
 * Sentry initialization for the browser. Called once from the root
 * layout — no-ops if NEXT_PUBLIC_SENTRY_DSN is unset, so leaving the
 * env var blank fully disables Sentry without touching this file.
 */

import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!dsn) return;

  const env = process.env.NEXT_PUBLIC_SENTRY_ENV || process.env.NODE_ENV || 'production';
  const release = process.env.NEXT_PUBLIC_SENTRY_RELEASE;

  Sentry.init({
    dsn,
    environment: env,
    release,
    tracesSampleRate: env === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    ignoreErrors: [
      'JWT expired',
      'Invalid token',
      'Network Error',
      'AbortError',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.Cookie;
      }
      return event;
    },
  });

  initialized = true;
}

export function captureException(
  error: unknown,
  context?: { tags?: Record<string, string>; extra?: Record<string, unknown> }
): void {
  if (!initialized) return;
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
    }
    if (context?.extra) {
      Object.entries(context.extra).forEach(([k, v]) => scope.setExtra(k, v));
    }
    Sentry.captureException(error);
  });
}

export function setUser(user: { id: string; email?: string } | null): void {
  if (!initialized) return;
  Sentry.setUser(user);
}

/** Tag the current Sentry scope (e.g. which settings tab is open). No-op if Sentry is off. */
export function setTag(key: string, value: string): void {
  if (!initialized) return;
  try { Sentry.setTag(key, value); } catch { /* never throw from instrumentation */ }
}

/**
 * Drop a breadcrumb so a later crash shows what the user did right before it
 * (e.g. "opened the Blocklist tab"). Crucial when prod stacks are minified.
 */
export function addBreadcrumb(message: string, data?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    Sentry.addBreadcrumb({ category: 'ui', level: 'info', message, data });
  } catch { /* never throw from instrumentation */ }
}
